import {
  useAdminCustomerLocationControllerGetAll,
  useAdminLeadControllerGetAll,
} from "service";

/**
 * Cross-entity search match counts
 *
 * The Customers and Leads pages each search their own table, but should also
 * surface how many matches exist on the OTHER entity for the same term (a
 * "Check X more results under Customers/Leads" prompt below the table).
 *
 * Rather than a dedicated backend endpoint, these reuse the other entity's
 * existing list endpoint with `limit: 1` and read `metaData.count` - every
 * list response already returns that count as part of pagination.
 */

const NO_MATCHES = 0;

/**
 * Match count for the Leads table, used by the Customers page.
 */
export const useMatchingLeadsCount = (search: string): number => {
  const trimmedSearch = search.trim();

  const { data } = useAdminLeadControllerGetAll(
    { queryParams: { search: trimmedSearch, limit: 1 } },
    { enabled: trimmedSearch.length > 0 },
  );

  return trimmedSearch ? (data?.metaData?.count ?? NO_MATCHES) : NO_MATCHES;
};

/**
 * Match count for the Customers table, used by the Leads page.
 *
 * Deliberately reuses the same locations-combo endpoint
 * (`useAdminCustomerLocationControllerGetAll`) that the Customers page
 * itself renders from, rather than the plain `/customers` endpoint, so this
 * count matches what the user will actually see after navigating there.
 *
 * Note: `metaData.count` here is a count of matching *locations*, not
 * unique customers - a customer with several properties that all match
 * the search term counts once per property. This is intentional, matching
 * the Figma design for ESR-1597 (the prompt reads "X more results", not
 * "X matching customers", so it doesn't claim a count of unique customers -
 * consistent with how the Customers page itself is organized around
 * locations rather than unique customer records).
 */
export const useMatchingCustomersCount = (search: string): number => {
  const trimmedSearch = search.trim();

  const { data } = useAdminCustomerLocationControllerGetAll(
    { queryParams: { search: trimmedSearch, limit: 1 } },
    { enabled: trimmedSearch.length > 0 },
  );

  return trimmedSearch ? (data?.metaData?.count ?? NO_MATCHES) : NO_MATCHES;
};
